"use client";

import React, { useEffect, useState, useCallback, useSyncExternalStore } from "react";
import {
  subscribeDebugEvents,
  getDebugEvents,
  clearDebugLog,
  requestSwDebugLog,
  type DebugEvent,
} from "@/lib/debug";
import { updateApp } from "@/lib/pwa-refresh";
import { BUILD_HASH, isNewBuildAvailable } from "@/lib/buildInfo";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { usePersistedState } from "@/lib/finance/usePersistedState";
import { LAUNCH_KEY, LAUNCH_TARGETS, type LaunchTarget } from "@/lib/launch-preference";
import type { FamilyApp } from "@/lib/apps";

interface CacheInfo {
  name: string;
  entries: { url: string; bytes: number }[];
  totalBytes: number;
}

interface StorageInfo {
  usage: number | null;
  quota: number | null;
  persisted: boolean | null;
}

interface SwInfo {
  supported: boolean;
  controlled: boolean;
  state: string;
  scriptURL: string | null;
  waiting: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("en-US", { hour12: false });
}

function shortUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const s = u.pathname + u.search;
    return s.length > 64 ? s.slice(0, 61) + "…" : s;
  } catch {
    return url.length > 64 ? url.slice(0, 61) + "…" : url;
  }
}

function sourceBadge(source: string) {
  let bg = "var(--green-bg)", fg = "var(--green)", label = source;
  if (source.startsWith("network→cache")) {
    bg = "#e8eefb"; fg = "#2a4d8f"; label = "network → " + source.split(":")[1];
  } else if (source === "network") {
    bg = "#e8eefb"; fg = "#2a4d8f";
  } else if (source.startsWith("cache-fallback")) {
    bg = "var(--warn-bg)"; fg = "var(--warn)"; label = "stale cache (net failed)";
  } else if (source.startsWith("cache:")) {
    label = source.slice(6);
  } else if (source === "offline") {
    bg = "#eceae4"; fg = "#6a5f52";
  } else if (source === "error") {
    bg = "#fbe8e8"; fg = "#8f2a2a";
  }
  return (
    <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: bg, color: fg, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

const section: React.CSSProperties = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "14px 16px",
  marginBottom: 14,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--mono)",
  color: "var(--text3)",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 10,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const mono: React.CSSProperties = { fontFamily: "var(--mono)", fontSize: 11 };

const smallButton: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--border)",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 10,
  fontFamily: "var(--mono)",
  color: "var(--text3)",
  cursor: "pointer",
};

/**
 * `full` (launcher): status + actions + cache inventory + fetch log.
 * `recovery` (login screen): status + the two actions ONLY. The login page
 * is reachable signed out, and on a device that was signed in before the
 * inventory and fetch log would list the previous session's request URLs
 * (trip ids, image names, shard keys) — structure, not content, but nothing
 * a stranger at the login screen needs. The two actions act only on this
 * browser's own caches and service worker, so they're safe to expose; they're
 * exactly what gets a device unstuck when a deployed fix has to land before
 * sign-in can work (lesson 28: a stale SW swallowing the OAuth hop).
 */
export default function DebugPanel({ variant = "full", apps = [] }: { variant?: "full" | "recovery"; apps?: FamilyApp[] } = {}) {
  const recovery = variant === "recovery";
  const [launch, setLaunch] = usePersistedState<LaunchTarget>(LAUNCH_KEY, "launcher");
  const launchTargets = LAUNCH_TARGETS.filter((t) => t.id === "launcher" || apps.some((a) => a.id === t.id));
  const events = useSyncExternalStore(subscribeDebugEvents, getDebugEvents, () => [] as DebugEvent[]);
  const [cacheInfos, setCacheInfos] = useState<CacheInfo[] | null>(null);
  const [storage, setStorage] = useState<StorageInfo | null>(null);
  const [swInfo, setSwInfo] = useState<SwInfo | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [busy, setBusy] = useState<"clearing" | "updating" | null>(null);
  const [updateNote, setUpdateNote] = useState<string | null>(null);
  const isOnline = useOnlineStatus();

  const refreshInventory = useCallback(async () => {
    // Cache inventory with per-entry sizes (not read in recovery mode — the
    // per-entry URLs are never shown there)
    try {
      const names = recovery ? [] : await caches.keys();
      const infos: CacheInfo[] = await Promise.all(
        names.map(async (name) => {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          const entries = await Promise.all(
            keys.map(async (req) => {
              let bytes = 0;
              try {
                const res = await cache.match(req);
                if (res) {
                  const cl = res.headers.get("content-length");
                  bytes = cl ? parseInt(cl, 10) : (await res.blob()).size;
                }
              } catch { /* unreadable entry */ }
              return { url: req.url, bytes };
            }),
          );
          entries.sort((a, b) => b.bytes - a.bytes);
          return { name, entries, totalBytes: entries.reduce((s, e) => s + e.bytes, 0) };
        }),
      );
      infos.sort((a, b) => b.totalBytes - a.totalBytes);
      setCacheInfos(infos);
    } catch {
      setCacheInfos([]);
    }

    // Storage estimate
    try {
      const est = await navigator.storage?.estimate?.();
      const persisted = (await navigator.storage?.persisted?.()) ?? null;
      setStorage({ usage: est?.usage ?? null, quota: est?.quota ?? null, persisted });
    } catch {
      setStorage(null);
    }

    // Service worker status
    try {
      if (!("serviceWorker" in navigator)) {
        setSwInfo({ supported: false, controlled: false, state: "unsupported", scriptURL: null, waiting: false });
      } else {
        const reg = await navigator.serviceWorker.getRegistration();
        setSwInfo({
          supported: true,
          controlled: !!navigator.serviceWorker.controller,
          state: reg?.active?.state ?? "none",
          scriptURL: reg?.active?.scriptURL ?? null,
          waiting: !!reg?.waiting,
        });
      }
    } catch {
      setSwInfo(null);
    }
  }, [recovery]);

  useEffect(() => {
    const t = setTimeout(() => void refreshInventory(), 0);
    // Pull events the SW recorded before this page was listening
    // (e.g. the navigation that loaded this very page).
    if (!recovery) requestSwDebugLog();
    return () => clearTimeout(t);
  }, [refreshInventory, recovery]);

  const kinds = ["all", ...Array.from(new Set(events.map((e) => e.kind))).sort()];
  const filtered = kindFilter === "all" ? events : events.filter((e) => e.kind === kindFilter);
  const shown = filtered.slice(-100).reverse();

  const cacheServed = filtered.filter((e) => e.source.startsWith("cache")).length;
  const networkServed = filtered.filter((e) => e.source.startsWith("network")).length;
  // Data volume: what actually crossed the network (≈ egress) vs what the
  // caches absorbed. "network→cache" counts as network — it was downloaded.
  const networkBytes = filtered.reduce((s, e) => s + (e.source.startsWith("network") ? e.bytes ?? 0 : 0), 0);
  const cacheBytes = filtered.reduce((s, e) => s + (e.source.startsWith("cache") ? e.bytes ?? 0 : 0), 0);

  // Update & reload — the explicit app-assets operation: SW update, page-shell
  // cache drop, reload onto the new build. Data and image caches untouched.
  // Checks the server's build hash FIRST and only proceeds when a newer build
  // is actually deployed — updating an already-current app would drop the
  // page-shell cache and pay a reload for nothing. A failed/offline check
  // reads as "no new build" (isNewBuildAvailable never throws), so it can
  // never wipe anything on a flaky connection.
  const runUpdateApp = async () => {
    setBusy("updating");
    setUpdateNote(null);
    if (await isNewBuildAvailable()) {
      setUpdateNote("New build found — updating…");
      void updateApp(); // navigates away — keep the busy state up
      return;
    }
    setUpdateNote(
      isOnline
        ? `Already on the latest build (${BUILD_HASH || "dev"}) — nothing updated.`
        : "Can't check for updates while offline — nothing updated.",
    );
    setBusy(null);
  };

  // Clear all caches — the from-scratch reset: unregister the service worker
  // and delete every Cache Storage cache (data, images, page shells, auth,
  // debug log), then reload. The SW re-registers and repopulates its precache
  // on the next load; data refetches on visit. Online-only: offline, wiping
  // the precache would brick the session until connectivity returns.
  const runClearAllCaches = async () => {
    if (!window.confirm("Clear every cache and reload? All data and images will re-download from scratch.")) return;
    setBusy("clearing");
    try {
      const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.all(regs.map((r) => r.unregister()));
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    } finally {
      window.location.reload();
    }
  };

  return (
    <div>
      {/* Storage + SW status */}
      <div style={section}>
        <div style={sectionTitle}>
          <span>Storage & Service Worker</span>
          <button
            style={{ ...smallButton, opacity: busy ? 0.6 : 1 }}
            disabled={!!busy}
            onClick={() => void runUpdateApp()}
            title="Check the server for a newer app build; only updates (service worker update + reload) if one is deployed. Data caches untouched."
          >
            {busy === "updating" ? "Checking…" : "Update & reload app ↺"}
          </button>
        </div>
        <div style={{ ...mono, color: "var(--text2)", lineHeight: 1.9 }}>
          <div>
            Storage used:{" "}
            <strong>{storage?.usage != null ? formatBytes(storage.usage) : "n/a"}</strong>
            {storage?.quota != null && <> of {formatBytes(storage.quota)} quota ({((storage.usage! / storage.quota) * 100).toFixed(2)}%)</>}
            {storage?.persisted != null && <> · persistent: {storage.persisted ? "yes" : "no"}</>}
          </div>
          <div>
            Service worker:{" "}
            <strong>{swInfo ? (swInfo.supported ? swInfo.state : "unsupported") : "…"}</strong>
            {swInfo?.supported && (
              <>
                {" "}· controlling this page: {swInfo.controlled ? "yes" : "no"}
                {swInfo.waiting && " · update waiting"}
              </>
            )}
          </div>
          <div>
            Build: <strong>{BUILD_HASH || "dev"}</strong>
          </div>
          {updateNote && <div style={{ color: "var(--text3)" }}>{updateNote}</div>}
        </div>
      </div>

      {recovery && (
        <div style={section}>
          <div style={sectionTitle}>
            <span>Caches</span>
            <button
              style={{ ...smallButton, opacity: busy || !isOnline ? 0.6 : 1 }}
              disabled={!!busy || !isOnline}
              onClick={() => void runClearAllCaches()}
              title={
                isOnline
                  ? "Delete every cache and unregister the service worker — the app refetches everything from scratch."
                  : "Unavailable offline — clearing caches would leave nothing to serve the app from."
              }
            >
              {busy === "clearing" ? "Clearing…" : "Clear all caches"}
            </button>
          </div>
          <div style={{ ...mono, color: "var(--text3)" }}>
            Stuck signing in? Try “Update & reload app” first; “Clear all caches” is the from-scratch reset (it also wipes this device’s offline data).
          </div>
        </div>
      )}
      {recovery ? null : (
      <>
      {/* Open by default — which app the Home Screen install boots into */}
      {launchTargets.length > 1 && (
        <div style={section}>
          <div style={sectionTitle}>
            <span>Open by default</span>
          </div>
          <div style={{ display: "flex", gap: 8 }} role="radiogroup" aria-label="Open by default">
            {launchTargets.map((t) => {
              const active = launch === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-launch={t.id}
                  onClick={() => setLaunch(t.id)}
                  style={{
                    ...smallButton,
                    flex: 1,
                    background: active ? "var(--text)" : "transparent",
                    color: active ? "var(--card)" : "var(--text2)",
                    borderColor: active ? "var(--text)" : "var(--border)",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div style={{ ...mono, color: "var(--text3)", marginTop: 8 }}>
            Applies when the app is launched from the Home Screen (a cold start). Switching back to it resumes where you left off.
          </div>
        </div>
      )}

      {/* Cache inventory */}
      <div style={section}>
        <div style={sectionTitle}>
          <span>
            Caches {cacheInfos ? `(${cacheInfos.length})` : ""}
            {cacheInfos && cacheInfos.length > 0 && (
              <span style={{ color: "var(--text3)", fontWeight: 400 }}>
                {" · "}
                {cacheInfos.reduce((s, c) => s + c.entries.length, 0)} entries ·{" "}
                {formatBytes(cacheInfos.reduce((s, c) => s + c.totalBytes, 0))} total
              </span>
            )}
          </span>
          <button
            style={{ ...smallButton, opacity: busy || !isOnline ? 0.6 : 1 }}
            disabled={!!busy || !isOnline}
            onClick={() => void runClearAllCaches()}
            title={
              isOnline
                ? "Delete every cache and unregister the service worker — the app refetches everything from scratch."
                : "Unavailable offline — clearing caches would leave nothing to serve the app from."
            }
          >
            {busy === "clearing" ? "Clearing…" : "Clear all caches"}
          </button>
        </div>
        {!cacheInfos && <div style={{ ...mono, color: "var(--text3)" }}>Reading caches…</div>}
        {cacheInfos?.length === 0 && <div style={{ ...mono, color: "var(--text3)" }}>No caches (Cache API unavailable or empty).</div>}
        {cacheInfos?.map((c) => {
          const isOpen = expanded.has(c.name);
          return (
            <div key={c.name} style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
              <button
                onClick={() => {
                  const next = new Set(expanded);
                  if (isOpen) next.delete(c.name); else next.add(c.name);
                  setExpanded(next);
                }}
                style={{ ...mono, display: "flex", justifyContent: "space-between", width: "100%", background: "none", border: "none", cursor: "pointer", color: "var(--text)", padding: 0 }}
              >
                <span style={{ fontWeight: 700 }}>{isOpen ? "▾" : "▸"} {c.name}</span>
                <span style={{ color: "var(--text3)" }}>{c.entries.length} {c.entries.length === 1 ? "entry" : "entries"} · {formatBytes(c.totalBytes)}</span>
              </button>
              {isOpen && (
                <div style={{ marginTop: 6, overflowX: "auto" }}>
                  {c.entries.map((e) => (
                    <div key={e.url} style={{ ...mono, display: "flex", justifyContent: "space-between", gap: 12, color: "var(--text2)", padding: "2px 0 2px 16px", whiteSpace: "nowrap" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{shortUrl(e.url)}</span>
                      <span style={{ color: "var(--text3)", flexShrink: 0 }}>{formatBytes(e.bytes)}</span>
                    </div>
                  ))}
                  {c.entries.length === 0 && <div style={{ ...mono, color: "var(--text3)", paddingLeft: 16 }}>empty</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fetch log */}
      <div style={section}>
        <div style={sectionTitle}>
          <span>
            Fetch log ({filtered.length}
            {filtered.length > 0 && <> · {cacheServed} cache / {networkServed} network</>})
            {filtered.length > 0 && (
              <span style={{ display: "block", textTransform: "none", letterSpacing: 0, marginTop: 2 }}>
                ↓ {formatBytes(networkBytes)} from network · {formatBytes(cacheBytes)} served from cache
              </span>
            )}
          </span>
          <span style={{ display: "flex", gap: 6 }}>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              style={{ ...smallButton, appearance: "auto" }}
            >
              {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <button style={smallButton} onClick={() => void clearDebugLog()}>Clear</button>
          </span>
        </div>
        {shown.length === 0 && (
          <div style={{ ...mono, color: "var(--text3)" }}>
            No fetch events yet. Open a trip, then come back — every data, image,
            page, and sync request will show up here with where it was
            served from.
          </div>
        )}
        {shown.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
              <thead>
                <tr>
                  {["time", "kind", "request", "served from", "size", "ms", "status"].map((h) => (
                    <th key={h} style={{ ...mono, textAlign: "left", color: "var(--text4)", fontWeight: 600, padding: "2px 10px 6px 0", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => (
                  <React.Fragment key={e.id}>
                    <tr
                      style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}
                      onClick={() => setExpandedRow(expandedRow === e.id ? null : e.id)}
                    >
                      <td style={{ ...mono, color: "var(--text3)", padding: "4px 10px 4px 0", whiteSpace: "nowrap" }}>{formatTime(e.ts)}</td>
                      <td style={{ ...mono, color: "var(--text2)", padding: "4px 10px 4px 0", whiteSpace: "nowrap" }}>
                        {e.kind}
                        <span style={{ color: "var(--text4)" }}> · {e.origin}</span>
                      </td>
                      <td style={{ ...mono, color: "var(--text2)", padding: "4px 10px 4px 0", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shortUrl(e.url)}</td>
                      <td style={{ padding: "4px 10px 4px 0" }}>{sourceBadge(e.source)}</td>
                      <td style={{ ...mono, color: "var(--text3)", padding: "4px 10px 4px 0", textAlign: "right", whiteSpace: "nowrap" }}>{e.bytes != null ? formatBytes(e.bytes) : ""}</td>
                      <td style={{ ...mono, color: "var(--text3)", padding: "4px 10px 4px 0", textAlign: "right" }}>{e.durationMs ?? ""}</td>
                      <td style={{ ...mono, color: "var(--text3)", padding: "4px 0" }}>{e.status ?? ""}</td>
                    </tr>
                    {expandedRow === e.id && (
                      <tr>
                        <td colSpan={7} style={{ ...mono, background: "var(--bg)", color: "var(--text2)", padding: "8px 10px", lineHeight: 1.7, wordBreak: "break-all" }}>
                          <div><span style={{ color: "var(--text4)" }}>url</span> {e.url}</div>
                          <div><span style={{ color: "var(--text4)" }}>served from</span> {e.source}</div>
                          {e.detail && <div><span style={{ color: "var(--text4)" }}>detail</span> {e.detail}</div>}
                          <div>
                            <span style={{ color: "var(--text4)" }}>origin</span> {e.origin}
                            {e.bytes != null && <> · {formatBytes(e.bytes)}</>}
                            {e.durationMs != null && <> · {e.durationMs}ms</>}
                            {e.status != null && <> · HTTP {e.status}</>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ ...mono, color: "var(--text4)", marginTop: 8 }}>
          Tap a row for full URL, strategy, and error details. Log survives
          reloads and Sync Now (stored in the &quot;debug-log&quot; cache).
        </div>
      </div>
      </>
      )}
    </div>
  );
}
